/**
 * Filter rules at first sight (piece 32, Filter rules): a rule's criteria all
 * match, its actions run, and the rules run in `sortOrder` until one says stop.
 * 🔨 A rule never targets Quarantine and never runs on a quarantined message,
 * because a rule that can quarantine mail is a rule that can hide mail.
 * 🔒 The precedence this locks in, since the specification gives none: rules
 * run lowest `sortOrder` first; the first Move to Folder or Delete a message meets
 * is where it goes and a later one is ignored; Mark read and Flag accumulate.
 */

export const FILTER_FIELDS = ['From', 'To', 'Subject', 'Body', 'HasAttachment'] as const;
export type FilterFieldValue = (typeof FILTER_FIELDS)[number];
export const FILTER_COMPARATORS = ['Contains', 'Equals', 'StartsWith', 'EndsWith', 'IsTrue'] as const;
export type FilterComparatorValue = (typeof FILTER_COMPARATORS)[number];
export const FILTER_ACTION_KINDS = ['MoveToFolder', 'MarkRead', 'Flag', 'Forward', 'Delete'] as const;
export type FilterActionKindValue = (typeof FILTER_ACTION_KINDS)[number];

export interface FilterCriterionRow {
  field: FilterFieldValue;
  comparator: FilterComparatorValue;
  value: string;
}

export interface FilterActionRow {
  kind: FilterActionKindValue;
  targetFolderId: string | null;
  forwardTo: string | null;
}

export interface FilterRuleRow {
  id: string;
  sortOrder: number;
  isActive: boolean;
  stopProcessing: boolean;
  skipInbox: boolean;
  /** Decision 1622: a fired rule with the toggle asks first sight to store a suggested record; absent reads as off. */
  suggestTagToRecord?: boolean;
  criteria: readonly FilterCriterionRow[];
  actions: readonly FilterActionRow[];
}

/** What a criterion reads about the message. */
export interface FilterFacts {
  from: string;
  to: readonly string[];
  subject: string;
  body: string;
  hasAttachment: boolean;
}

function compare(haystack: string, comparator: FilterComparatorValue, value: string): boolean {
  const h = haystack.toLowerCase();
  const v = value.trim().toLowerCase();
  switch (comparator) {
    case 'Contains':
      return v !== '' && h.includes(v);
    case 'Equals':
      return h === v;
    case 'StartsWith':
      return v !== '' && h.startsWith(v);
    case 'EndsWith':
      return v !== '' && h.endsWith(v);
    default:
      return false;
  }
}

export function filterCriterionMatches(criterion: FilterCriterionRow, facts: FilterFacts): boolean {
  switch (criterion.field) {
    case 'HasAttachment':
      return criterion.comparator === 'IsTrue' ? facts.hasAttachment : !facts.hasAttachment;
    case 'From':
      return compare(facts.from, criterion.comparator, criterion.value);
    case 'To':
      return facts.to.some((address) => compare(address, criterion.comparator, criterion.value));
    case 'Subject':
      return compare(facts.subject, criterion.comparator, criterion.value);
    case 'Body':
      return compare(facts.body, criterion.comparator, criterion.value);
  }
}

/** A rule with no criteria matches nothing: an empty rule is a rule nobody finished writing. */
export function filterRuleMatches(rule: FilterRuleRow, facts: FilterFacts): boolean {
  return rule.isActive && rule.criteria.length > 0 && rule.criteria.every((c) => filterCriterionMatches(c, facts));
}

/** What the rules decided for one message, in the order they ran. */
export interface FilterOutcome {
  /** The folder the first Move to Folder named, or null. */
  moveTo: string | null;
  /** True where the first placing action was Delete, which moves the message to Trash. */
  delete: boolean;
  markRead: boolean;
  flag: boolean;
  forwardTo: string[];
  skipInbox: boolean;
  /** Decision 1622: a fired rule carried Suggest Tag to Record. */
  suggestTagToRecord: boolean;
  firedRuleIds: string[];
}

export function runFilterRules(rules: readonly FilterRuleRow[], facts: FilterFacts, quarantineFolderIds: ReadonlySet<string>): FilterOutcome {
  const outcome: FilterOutcome = { moveTo: null, delete: false, markRead: false, flag: false, forwardTo: [], skipInbox: false, suggestTagToRecord: false, firedRuleIds: [] };
  let placed = false;
  for (const rule of [...rules].sort((a, b) => a.sortOrder - b.sortOrder || a.id.localeCompare(b.id))) {
    if (!filterRuleMatches(rule, facts)) continue;
    outcome.firedRuleIds.push(rule.id);
    if (rule.skipInbox) outcome.skipInbox = true;
    if (rule.suggestTagToRecord) outcome.suggestTagToRecord = true;
    for (const action of rule.actions) {
      if (action.kind === 'MarkRead') outcome.markRead = true;
      if (action.kind === 'Flag') outcome.flag = true;
      if (action.kind === 'Forward' && action.forwardTo) outcome.forwardTo.push(action.forwardTo);
      if (placed) continue;
      // 🔨 The database refuses a Quarantine target; a row that got past it is ignored here too.
      if (action.kind === 'MoveToFolder' && action.targetFolderId && !quarantineFolderIds.has(action.targetFolderId)) {
        outcome.moveTo = action.targetFolderId;
        placed = true;
      }
      if (action.kind === 'Delete') {
        outcome.delete = true;
        placed = true;
      }
    }
    if (rule.stopProcessing) break;
  }
  return outcome;
}

/** The Filter Rules modal's routes (piece 32, Filter rules), relative to the API root. */
export const MAIL_FILTER_RULES_PATH = '/mail/filter-rules';
/** The order, written whole; a literal segment the server declares before a rule's own id. */
export const MAIL_FILTER_RULES_ORDER_PATH = `${MAIL_FILTER_RULES_PATH}/order`;
/** Run now: the person's rules over their own Inbox. */
export const MAIL_FILTER_RULES_RUN_PATH = `${MAIL_FILTER_RULES_PATH}/run`;
export const mailFilterRulePath = (id: string): string => `${MAIL_FILTER_RULES_PATH}/${id}`;

/**
 * 🔒 What the rules builder offers as an action (piece 32): Move to Folder, Mark
 * read and Flag, beside the two per-rule toggles Skip Inbox and Suggest Tag to
 * Record. 🔨 Never Quarantine, and never a forward or a delete from the builder:
 * a rule that sends mail out or throws it away is a rule that can lose it silently.
 */
export const FILTER_EDITOR_ACTION_KINDS = ['MoveToFolder', 'MarkRead', 'Flag'] as const satisfies readonly FilterActionKindValue[];

/** The comparators a field offers: the text comparators on a text field, and IsTrue alone on HasAttachment. */
export function comparatorsFor(field: FilterFieldValue): readonly FilterComparatorValue[] {
  return field === 'HasAttachment' ? ['IsTrue'] : ['Contains', 'Equals', 'StartsWith', 'EndsWith'];
}

/** One rule as the modal reads and writes it. */
export interface FilterRuleView {
  id: string;
  version: number;
  name: string;
  sortOrder: number;
  isActive: boolean;
  stopProcessing: boolean;
  skipInbox: boolean;
  suggestTagToRecord: boolean;
  criteria: FilterCriterionRow[];
  /** The folder the rule moves a message to, in the person's own mailbox; null where it moves nothing. */
  moveToFolderId: string | null;
  markRead: boolean;
  flag: boolean;
}

export type FilterRuleInput = Omit<FilterRuleView, 'id' | 'version' | 'sortOrder'> & { version?: number };

/** The modal's answer: the person's rules in order, and the folders of their own mailbox a rule may move to. */
export interface FilterRulesAnswer {
  rules: FilterRuleView[];
  folders: { id: string; name: string }[];
}

export interface FilterRunReport {
  considered: number;
  changed: number;
}

/**
 * 🔨 A rule the builder refuses, in words: no name, no criterion, a value missing
 * on a text criterion, nothing to do, or a target that is not one of the folders
 * offered — Quarantine never is.
 */
export function filterRuleProblem(input: FilterRuleInput, offeredFolderIds: ReadonlySet<string>): string | null {
  if (!input.name.trim()) return 'a rule has a name';
  if (input.criteria.length === 0) return 'a rule names at least one condition';
  for (const c of input.criteria) {
    if (!(FILTER_FIELDS as readonly string[]).includes(c.field)) return `${c.field} is not a field a rule reads`;
    if (!comparatorsFor(c.field).includes(c.comparator)) return `a ${c.field} condition does not compare that way`;
    if (c.field !== 'HasAttachment' && !c.value.trim()) return 'each condition names what it looks for';
  }
  if (!input.moveToFolderId && !input.markRead && !input.flag && !input.skipInbox && !input.suggestTagToRecord) return 'a rule does at least one thing';
  if (input.moveToFolderId && !offeredFolderIds.has(input.moveToFolderId)) return 'a rule moves mail to one of your own folders, and never to Quarantine';
  return null;
}
